import { storageRef } from '../../config/firebase'
import { listBlogTags, upsertBlogTag } from './blogApi'
import { getLocalTag, upsertLocalTag, createLocalId } from './blogLocalStore'

// Cache successful uploads across retries without changing the editor's snapshot.
export const createBlogDraftPreparer = () => {
  const uploads = new Map()
  const prepareMedia = async (value, materialId) => {
    if (Array.isArray(value)) {
      const result = []
      for (const item of value) result.push(await prepareMedia(item, materialId))
      return result
    }
    if (!value || typeof value !== 'object') return value
    const urlKey = typeof value.imageUrl === 'string' ? 'imageUrl' : typeof value.url === 'string' ? 'url' : null
    const url = urlKey && value[urlKey]
    if (url && /^(data:|blob:)/i.test(url)) {
      const cacheKey = `${materialId}:${url}`
      let uploaded = uploads.get(cacheKey)
      if (!uploaded) {
        const ref = storageRef.child(`blog/${materialId}/media/${createLocalId()}`)
        if (url.startsWith('data:')) {
          await ref.putString(url, 'data_url')
        } else {
          const response = await fetch(url)
          if (!response.ok) throw new Error('Не вдалося прочитати локальне медіа')
          await ref.put(await response.blob())
        }
        uploaded = { [urlKey]: await ref.getDownloadURL(), storagePath: ref.fullPath, fileName: value.fileName || null }
        uploads.set(cacheKey, uploaded)
      }
      return uploaded
    }
    if (value.needUpload && !url) throw new Error('Локальне фото недоступне. Додайте його повторно перед збереженням.')
    const result = {}
    for (const [key, child] of Object.entries(value)) {
      if (child !== undefined && !['file', 'needUpload', 'uploadProgress'].includes(key)) {
        result[key] = await prepareMedia(child, materialId)
      }
    }
    return result
  }

  return async (material) => {
    if (!material.draft?.title?.trim()) throw new Error('Вкажіть назву матеріалу')
    const draft = await prepareMedia(material.draft, material.id)
    const ids = [...new Set([draft.categoryId, ...(draft.tagIds || [])].filter(Boolean))]
    if (ids.length) {
      const remote = await listBlogTags()
      if (!remote.ok) throw new Error(remote.message)
      const terms = new Map(remote.tags.map((tag) => [tag.id, tag]))
      const resolved = new Map()
      for (const id of ids) {
        if (terms.has(id)) { resolved.set(id, id); continue }
        const local = getLocalTag(id)
        if (!local) throw new Error('Категорію або тег не знайдено: ' + id)
        const existing = remote.tags.find((tag) => tag.slug === local.slug)
        if (existing) {
          if ((existing.kind || 'tag') !== (local.kind || 'tag')) throw new Error('Slug зайнятий іншим типом категорії або тега')
          resolved.set(id, existing.id)
          upsertLocalTag(existing)
          continue
        }
        const saved = await upsertBlogTag({ tagId: id, name: local.name, slug: local.slug, kind: local.kind || 'tag', order: local.order || 0, createIfMissing: true })
        if (!saved.ok) throw new Error(saved.message)
        terms.set(id, saved.tag)
        resolved.set(id, saved.tag.id)
        upsertLocalTag(saved.tag)
      }
      draft.categoryId = draft.categoryId ? resolved.get(draft.categoryId) : null
      draft.tagIds = [...new Set((draft.tagIds || []).map((id) => resolved.get(id)))]
    }
    return draft
  }
}
